import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { getAdminSession, useConfirmModal } from "./adminShared";

const fmtDate = (v) => {
    if (!v) return "—";
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return v;
    return d.toLocaleDateString("pt-BR");
};

const CONTRACT_TYPES = [
    { id: "clt", label: "CLT" },
    { id: "pj", label: "PJ" },
    { id: "estagio", label: "Estágio" },
    { id: "freelancer", label: "Freelancer" },
];
const ACCOUNT_TYPES = [
    { id: "corrente", label: "Corrente" },
    { id: "poupanca", label: "Poupança" },
    { id: "pix", label: "Pix" },
];
const PERMISSION_ROWS = [
    { key: "dashboard", label: "Dashboard" },
    { key: "tenants", label: "Clientes Registrados" },
    { key: "plans", label: "Gestão de Planos" },
    { key: "affiliates", label: "Afiliados" },
    { key: "audit_logs", label: "Logs de Auditoria" },
    { key: "terms", label: "Configurações" },
    { key: "seo", label: "SEO & Meta" },
    { key: "staff", label: "Funcionários" },
    { key: "links", label: "Links" },
];
const EMPTY_PERMISSIONS = PERMISSION_ROWS.reduce((acc, r) => ({ ...acc, [r.key]: { view: false, edit: false } }), {});
const EMPTY_FORM = {
    name: "", email: "", cargo: "", phone: "", start_date: "", active: true, avatar_url: "",
    contract_type: "clt", salary: "", bank: "", agency: "", account: "", account_type: "corrente",
    pix_key: "", financial_notes: "", permissions: EMPTY_PERMISSIONS,
};

/**
 * AdminStaffDetail — tela cheia de criação/edição de funcionário, em 4
 * camadas empilhadas. Layer 2 (financeiro) só aparece — no front E na
 * resposta do backend — pro super-admin real; funcionário com
 * "Funcionários: Editar" edita Layers 1/3/4, nunca a 2.
 */
const AdminStaffDetail = ({ staffId, onBack }) => {
    const { isSuperAdmin, permissions: viewerPerms } = getAdminSession();
    const viewerCanEditStaff = isSuperAdmin || !!viewerPerms?.staff?.edit;
    const { requestConfirm, ConfirmModalElement } = useConfirmModal();

    const [currentId, setCurrentId] = useState(staffId);
    const isNew = !currentId;
    const [staff, setStaff] = useState(null);
    const [form, setForm] = useState(EMPTY_FORM);
    const [loading, setLoading] = useState(!isNew);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [createdPassword, setCreatedPassword] = useState(null);
    const [showTimeModal, setShowTimeModal] = useState(false);

    const set = (patch) => setForm((f) => ({ ...f, ...patch }));

    const load = async (id) => {
        setLoading(true);
        try {
            const s = await api.get(`/admin/staff/${id}`);
            setStaff(s);
            setForm({
                name: s.name || "", email: s.email || "", cargo: s.cargo || "", phone: s.phone || "",
                start_date: s.start_date || "", active: s.active !== false, avatar_url: s.avatar_url || "",
                contract_type: s.contract_type || "clt", salary: s.salary ?? "", bank: s.bank || "",
                agency: s.agency || "", account: s.account || "", account_type: s.account_type || "corrente",
                pix_key: s.pix_key || "", financial_notes: s.financial_notes || "",
                permissions: { ...EMPTY_PERMISSIONS, ...(s.permissions || {}) },
            });
        } catch (e) {
            toast.error("Falha ao carregar funcionário", { description: e.message });
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { if (currentId) load(currentId); }, [currentId]);

    const togglePermView = (key) => {
        setForm((f) => {
            const cur = f.permissions[key] || { view: false, edit: false };
            const nextView = !cur.view;
            return { ...f, permissions: { ...f.permissions, [key]: { view: nextView, edit: nextView ? cur.edit : false } } };
        });
    };
    const togglePermEdit = (key) => {
        setForm((f) => {
            const cur = f.permissions[key] || { view: false, edit: false };
            const nextEdit = !cur.edit;
            return { ...f, permissions: { ...f.permissions, [key]: { view: nextEdit ? true : cur.view, edit: nextEdit } } };
        });
    };

    const buildBody = () => {
        const body = {
            name: form.name.trim(),
            cargo: form.cargo,
            phone: form.phone,
            start_date: form.start_date || null,
            active: form.active,
            avatar_url: form.avatar_url || null,
            permissions: form.permissions,
        };
        if (isNew) body.email = form.email.trim();
        if (isSuperAdmin) {
            Object.assign(body, {
                contract_type: form.contract_type,
                salary: Number(form.salary) || 0,
                bank: form.bank,
                agency: form.agency,
                account: form.account,
                account_type: form.account_type,
                pix_key: form.pix_key,
                financial_notes: form.financial_notes,
            });
        }
        return body;
    };

    const handleSave = async () => {
        if (!form.name.trim() || (isNew && !form.email.trim())) {
            toast.error("Preencha nome e e-mail"); return;
        }
        setSaving(true);
        try {
            if (isNew) {
                const res = await api.post("/admin/staff", buildBody());
                setCurrentId(res.id);
                setStaff(res);
                setCreatedPassword(res.temp_password);
                toast.success("Funcionário criado!");
            } else {
                const res = await api.patch(`/admin/staff/${currentId}`, buildBody());
                setStaff(res);
                toast.success("Funcionário atualizado");
            }
        } catch (e) {
            toast.error("Falha ao salvar", { description: e.message });
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = () => {
        requestConfirm({
            title: "Excluir funcionário",
            message: `Tem certeza que deseja excluir ${staff?.name}? O acesso é revogado imediatamente (o registro é mantido para histórico/auditoria).`,
            confirmLabel: "Excluir",
            danger: true,
            onConfirm: async () => {
                setDeleting(true);
                try {
                    await api.del(`/admin/staff/${currentId}`);
                    toast.success("Funcionário excluído");
                    onBack();
                } catch (e) {
                    toast.error("Falha ao excluir", { description: e.message });
                } finally {
                    setDeleting(false);
                }
            },
        });
    };

    const handleTimeRecordSave = async (record) => {
        const res = await api.post(`/admin/staff/${currentId}/time-record`, record);
        setStaff(res);
        toast.success("Ponto registrado");
    };

    if (loading) {
        return (
            <div className="space-y-6" data-testid="admin-staff-detail">
                <BackButton onBack={onBack} />
                <div className="py-16 text-center text-slate-500 text-sm">Carregando…</div>
            </div>
        );
    }

    const canSeeLayer4 = isSuperAdmin || viewerCanEditStaff;
    const canEditFields = isNew ? true : (isSuperAdmin || viewerCanEditStaff);

    return (
        <div className="space-y-6 max-w-4xl" data-testid="admin-staff-detail">
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <BackButton onBack={onBack} name={isNew ? "Novo Funcionário" : staff?.name} />
                {isSuperAdmin && !isNew && staff?.active && (
                    <button
                        data-testid="admin-staff-detail-delete"
                        onClick={handleDelete}
                        disabled={deleting}
                        className="min-h-[40px] px-4 rounded-xl border border-red-500/30 hover:border-red-500/60 text-red-300 hover:bg-red-500/10 font-semibold text-xs uppercase tracking-wider transition-all disabled:opacity-40"
                    >
                        <i className="fa-solid fa-trash mr-2"></i>Excluir funcionário
                    </button>
                )}
            </div>

            {createdPassword && (
                <div className="rounded-2xl border border-amber-500/30 bg-amber-500/10 p-4" data-testid="admin-staff-temp-password">
                    <div className="flex items-center justify-between mb-2">
                        <div className="text-amber-200 text-xs uppercase tracking-wider font-bold">
                            <i className="fa-solid fa-triangle-exclamation mr-1.5"></i>Senha temporária — anote agora
                        </div>
                        <button onClick={() => setCreatedPassword(null)} className="text-slate-400 hover:text-white">
                            <i className="fa-solid fa-xmark"></i>
                        </button>
                    </div>
                    <div className="flex items-center gap-2">
                        <code className="flex-1 px-3 py-2 rounded-lg bg-slate-950/80 text-white font-mono text-sm break-all">{createdPassword}</code>
                        <button
                            onClick={() => { navigator.clipboard.writeText(createdPassword); toast.success("Copiado!"); }}
                            className="shrink-0 h-10 w-10 rounded-lg bg-white/5 hover:bg-white/10 text-slate-300"
                        >
                            <i className="fa-solid fa-copy"></i>
                        </button>
                    </div>
                    <p className="text-[11px] text-slate-400 mt-2">Essa senha não será exibida novamente — repasse ao funcionário com segurança.</p>
                </div>
            )}

            {/* Layer 1 — Dados Pessoais */}
            <Layer icon="fa-id-card" title="Dados Pessoais" testid="staff-layer-personal">
                <div className="grid sm:grid-cols-2 gap-4">
                    <Field label="Nome completo *" value={form.name} onChange={(v) => set({ name: v })} testId="staff-name" disabled={!canEditFields} />
                    <Field label="E-mail (login) *" type="email" value={form.email} onChange={(v) => set({ email: v })} testId="staff-email" disabled={!isNew} />
                    <Field label="Cargo" value={form.cargo} onChange={(v) => set({ cargo: v })} testId="staff-cargo" placeholder="Ex: Suporte, Comercial" disabled={!canEditFields} />
                    <Field label="Telefone" value={form.phone} onChange={(v) => set({ phone: v })} testId="staff-phone" disabled={!canEditFields} />
                    <Field label="Data de início" type="date" value={form.start_date} onChange={(v) => set({ start_date: v })} testId="staff-start-date" disabled={!canEditFields} />
                    <Field label="Avatar (URL da imagem)" value={form.avatar_url} onChange={(v) => set({ avatar_url: v })} testId="staff-avatar" placeholder="https://…" disabled={!canEditFields} />
                </div>
                <div className="mt-4">
                    <label className="text-[10px] uppercase tracking-[0.25em] text-slate-500 block mb-1.5">Status</label>
                    <div className="inline-flex rounded-xl border border-white/10 overflow-hidden">
                        {[["true", "Ativo", true], ["false", "Inativo", false]].map(([id, label, val]) => (
                            <button
                                key={id}
                                type="button"
                                data-testid={`staff-status-${id}`}
                                disabled={!canEditFields}
                                onClick={() => set({ active: val })}
                                className={`px-4 py-2 text-xs font-semibold uppercase tracking-wider transition-all ${
                                    form.active === val ? "bg-indigo-500 text-white" : "text-slate-400 hover:text-white"
                                } disabled:opacity-50`}
                            >
                                {label}
                            </button>
                        ))}
                    </div>
                </div>
            </Layer>

            {/* Layer 2 — Dados Financeiros (só super-admin real) */}
            {isSuperAdmin && (
                <Layer icon="fa-sack-dollar" title="Dados Financeiros" testid="staff-layer-financial" hint="Visível apenas ao super-admin">
                    <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                            <label className="text-[10px] uppercase tracking-[0.25em] text-slate-500 block mb-1.5">Tipo de contrato</label>
                            <select value={form.contract_type} onChange={(e) => set({ contract_type: e.target.value })} data-testid="staff-contract-type"
                                className="w-full min-h-[44px] rounded-xl border border-white/10 bg-slate-950/60 px-3 text-sm text-white outline-none focus:border-indigo-500/60">
                                {CONTRACT_TYPES.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                            </select>
                        </div>
                        <Field label="Salário / Valor (R$)" type="number" value={form.salary} onChange={(v) => set({ salary: v })} testId="staff-salary" />
                        <Field label="Banco" value={form.bank} onChange={(v) => set({ bank: v })} testId="staff-bank" />
                        <Field label="Agência" value={form.agency} onChange={(v) => set({ agency: v })} testId="staff-agency" />
                        <Field label="Conta" value={form.account} onChange={(v) => set({ account: v })} testId="staff-account" />
                        <div>
                            <label className="text-[10px] uppercase tracking-[0.25em] text-slate-500 block mb-1.5">Tipo de conta</label>
                            <select value={form.account_type} onChange={(e) => set({ account_type: e.target.value })} data-testid="staff-account-type"
                                className="w-full min-h-[44px] rounded-xl border border-white/10 bg-slate-950/60 px-3 text-sm text-white outline-none focus:border-indigo-500/60">
                                {ACCOUNT_TYPES.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                            </select>
                        </div>
                        <Field label="Chave Pix" value={form.pix_key} onChange={(v) => set({ pix_key: v })} testId="staff-pix" />
                    </div>
                    <div className="mt-4">
                        <Field label="Observações financeiras" textarea value={form.financial_notes} onChange={(v) => set({ financial_notes: v })} testId="staff-financial-notes" />
                    </div>
                </Layer>
            )}

            {/* Layer 3 — Registro de Ponto */}
            {!isNew && (
                <TimeRecordLayer
                    records={staff?.time_records || []}
                    canEdit={canEditFields}
                    onAddClick={() => setShowTimeModal(true)}
                />
            )}

            {/* Layer 4 — Permissões (super-admin ou staff com Funcionários:Editar) */}
            {canSeeLayer4 && (
                <Layer icon="fa-user-shield" title="Permissões do Sistema" testid="staff-layer-permissions">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-[10px] uppercase tracking-[0.25em] text-slate-500 border-b border-white/5">
                                    <th className="text-left py-2 pr-4">Módulo</th>
                                    <th className="text-center py-2 px-3">Ver</th>
                                    <th className="text-center py-2 px-3">Editar</th>
                                </tr>
                            </thead>
                            <tbody>
                                {PERMISSION_ROWS.map((row) => {
                                    const p = form.permissions[row.key] || { view: false, edit: false };
                                    return (
                                        <tr key={row.key} className="border-b border-white/5 last:border-0">
                                            <td className="py-2.5 pr-4 text-slate-200">{row.label}</td>
                                            <td className="py-2.5 px-3 text-center">
                                                <input
                                                    type="checkbox"
                                                    checked={p.view}
                                                    disabled={!canEditFields}
                                                    onChange={() => togglePermView(row.key)}
                                                    data-testid={`staff-perm-${row.key}-view`}
                                                    className="h-4 w-4 accent-indigo-500 cursor-pointer disabled:opacity-40"
                                                />
                                            </td>
                                            <td className="py-2.5 px-3 text-center">
                                                <input
                                                    type="checkbox"
                                                    checked={p.edit}
                                                    disabled={!canEditFields}
                                                    onChange={() => togglePermEdit(row.key)}
                                                    data-testid={`staff-perm-${row.key}-edit`}
                                                    className="h-4 w-4 accent-indigo-500 cursor-pointer disabled:opacity-40"
                                                />
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </Layer>
            )}

            {canEditFields && (
                <div className="flex justify-end">
                    <button
                        data-testid="admin-staff-save"
                        onClick={handleSave}
                        disabled={saving}
                        className="min-h-[44px] px-6 rounded-xl bg-indigo-500 hover:bg-indigo-400 text-white font-semibold text-xs uppercase tracking-wider transition-all disabled:opacity-40"
                    >
                        {saving ? <><i className="fa-solid fa-spinner fa-spin mr-2"></i>Salvando…</> : (isNew ? "Criar funcionário" : "Salvar alterações")}
                    </button>
                </div>
            )}

            {showTimeModal && (
                <TimeRecordModal onClose={() => setShowTimeModal(false)} onSave={handleTimeRecordSave} />
            )}

            {ConfirmModalElement}
        </div>
    );
};

const BackButton = ({ onBack, name }) => (
    <div className="flex items-center gap-3">
        <button
            data-testid="staff-back-btn"
            onClick={onBack}
            className="h-10 w-10 rounded-xl border border-white/10 bg-slate-900/60 hover:bg-slate-900 hover:border-indigo-500/40 text-slate-300 hover:text-white transition-all flex items-center justify-center shrink-0"
            title="Voltar para a lista"
        >
            <i className="fa-solid fa-arrow-left"></i>
        </button>
        <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-indigo-400">Funcionários</div>
            {name && <h1 className="salon-heading text-2xl text-white mt-0.5">{name}</h1>}
        </div>
    </div>
);

const Layer = ({ icon, title, testid, hint, children }) => (
    <section data-testid={testid} className="rounded-2xl border border-white/5 bg-slate-900/40 backdrop-blur-xl p-5">
        <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
                <i className={`fa-solid ${icon} text-indigo-400 text-sm`}></i>
                <h2 className="text-sm font-semibold text-white">{title}</h2>
            </div>
            {hint && <span className="text-[10px] uppercase tracking-wider text-amber-300/80"><i className="fa-solid fa-lock mr-1"></i>{hint}</span>}
        </div>
        {children}
    </section>
);

const Field = ({ label, value, onChange, testId, placeholder, type = "text", textarea, disabled }) => {
    const Tag = textarea ? "textarea" : "input";
    return (
        <label className="block">
            <span className="text-[10px] uppercase tracking-[0.25em] text-slate-500">{label}</span>
            <Tag
                data-testid={testId}
                type={textarea ? undefined : type}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                disabled={disabled}
                rows={textarea ? 3 : undefined}
                className={`mt-1.5 w-full rounded-xl border border-white/10 bg-slate-950/60 px-4 py-2.5 text-sm text-white placeholder:text-slate-600 outline-none focus:border-indigo-500/60 transition-colors disabled:opacity-50 ${textarea ? "resize-y" : "min-h-[44px]"}`}
            />
        </label>
    );
};

// ─── Layer 3: Registro de Ponto ─────────────────────────────────────────
const TimeRecordLayer = ({ records, canEdit, onAddClick }) => {
    const last30 = records
        .slice()
        .sort((a, b) => (a.date < b.date ? 1 : -1))
        .slice(0, 30);

    const now = new Date();
    const monthPrefix = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
    const monthRecords = records.filter((r) => (r.date || "").startsWith(monthPrefix));
    const totalHoursMonth = monthRecords.reduce((s, r) => s + (r.total_hours || 0), 0);
    // Estimativa simples: 8h por dia útil (seg-sex) já decorrido no mês corrente.
    let expectedHours = 0;
    for (let d = 1; d <= now.getDate(); d++) {
        const day = new Date(now.getFullYear(), now.getMonth(), d).getDay();
        if (day !== 0 && day !== 6) expectedHours += 8;
    }
    const balance = totalHoursMonth - expectedHours;

    return (
        <Layer icon="fa-clock" title="Registro de Ponto" testid="staff-layer-timeclock">
            <div className="grid grid-cols-3 gap-3 mb-4">
                <MiniStat label="Horas no mês" value={`${totalHoursMonth.toFixed(1)}h`} />
                <MiniStat label="Esperado (dias úteis)" value={`${expectedHours.toFixed(1)}h`} />
                <MiniStat label="Saldo" value={`${balance >= 0 ? "+" : ""}${balance.toFixed(1)}h`} color={balance >= 0 ? "#10b981" : "#ef4444"} />
            </div>

            {canEdit && (
                <button
                    data-testid="staff-add-time-record"
                    onClick={onAddClick}
                    className="mb-4 min-h-[40px] px-4 rounded-xl border border-indigo-500/30 bg-indigo-500/10 text-indigo-200 hover:bg-indigo-500/20 text-xs uppercase tracking-wider font-semibold transition-all"
                >
                    <i className="fa-solid fa-plus mr-2"></i>Registrar ponto manualmente
                </button>
            )}

            <div className="overflow-x-auto rounded-xl border border-white/5">
                <table className="w-full text-xs">
                    <thead>
                        <tr className="text-[10px] uppercase tracking-[0.2em] text-slate-500 bg-slate-950/60">
                            <th className="text-left py-2 px-3">Data</th>
                            <th className="text-left py-2 px-3">Entrada</th>
                            <th className="text-left py-2 px-3">Saída</th>
                            <th className="text-right py-2 px-3">Total</th>
                            <th className="text-left py-2 px-3">Observação</th>
                        </tr>
                    </thead>
                    <tbody>
                        {last30.length === 0 ? (
                            <tr><td colSpan={5} className="text-center py-8 text-slate-500">Nenhum registro nos últimos 30 dias.</td></tr>
                        ) : last30.map((r, i) => (
                            <tr key={i} className="border-t border-white/5">
                                <td className="py-2 px-3 text-slate-300">{fmtDate(r.date)}</td>
                                <td className="py-2 px-3 font-mono text-slate-300">{r.clock_in || "—"}</td>
                                <td className="py-2 px-3 font-mono text-slate-300">{r.clock_out || "—"}</td>
                                <td className="py-2 px-3 text-right font-mono text-white">{r.total_hours ? `${r.total_hours}h` : "—"}</td>
                                <td className="py-2 px-3 text-slate-500 truncate max-w-[200px]">{r.note || "—"}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <p className="text-[11px] text-slate-600 mt-3">
                Registro por batida do próprio funcionário chega em uma próxima versão — hoje os lançamentos são manuais.
            </p>
        </Layer>
    );
};

const MiniStat = ({ label, value, color = "#6366f1" }) => (
    <div className="rounded-xl border border-white/5 bg-slate-950/40 p-3">
        <div className="text-[9px] uppercase tracking-wider text-slate-500">{label}</div>
        <div className="text-lg font-mono mt-0.5" style={{ color }}>{value}</div>
    </div>
);

const TimeRecordModal = ({ onClose, onSave }) => {
    const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
    const [clockIn, setClockIn] = useState("");
    const [clockOut, setClockOut] = useState("");
    const [note, setNote] = useState("");
    const [saving, setSaving] = useState(false);

    const submit = async () => {
        if (!date) { toast.error("Informe a data"); return; }
        setSaving(true);
        try {
            await onSave({ date, clock_in: clockIn || null, clock_out: clockOut || null, note });
            onClose();
        } catch (e) {
            toast.error("Falha ao registrar ponto", { description: e.message });
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
            <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md rounded-2xl border border-white/10 bg-slate-950 p-6 space-y-3" data-testid="staff-time-record-modal">
                <h3 className="salon-heading text-xl text-white">Registrar ponto manualmente</h3>
                <Field label="Data" type="date" value={date} onChange={setDate} testId="staff-time-record-date" />
                <div className="grid grid-cols-2 gap-3">
                    <Field label="Entrada" type="time" value={clockIn} onChange={setClockIn} testId="staff-time-record-in" />
                    <Field label="Saída" type="time" value={clockOut} onChange={setClockOut} testId="staff-time-record-out" />
                </div>
                <Field label="Observação / Motivo" textarea value={note} onChange={setNote} testId="staff-time-record-note" />
                <div className="flex gap-2 pt-2">
                    <button onClick={onClose} className="flex-1 rounded-xl border border-white/10 text-slate-300 py-2.5 text-sm">Cancelar</button>
                    <button
                        data-testid="staff-time-record-save"
                        onClick={submit}
                        disabled={saving}
                        className="flex-1 rounded-xl bg-indigo-500 hover:bg-indigo-400 text-white font-bold py-2.5 text-sm disabled:opacity-40"
                    >
                        {saving ? "Salvando…" : "Registrar"}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AdminStaffDetail;
